/**
 * The print export: one ZIP from which a print shop sets the business cards
 * of a list of colleagues with Marketing's InDesign template (Data Merge).
 *
 * - `cards.txt`: one row per colleague with the face's texts, exactly as the
 *   digital card shows them (card-face.ts), and an `@QR` image column. Tab
 *   separated UTF-16 with BOM, the encoding Data Merge reads without
 *   mangling umlauts or accents.
 * - `<Name>-qr.pdf`: the QR as vector PDF in Norican Blue CMYK, white margin
 *   included, next to `cards.txt` so the `@QR` column can name it bare.
 * - `svg/<Name>-qr.svg`: the same symbol in RGB, as "QR as SVG" downloads it.
 * - `README.txt`: how to merge, how big to print.
 *
 * Text and QR come from the same card record, so a printed card cannot say
 * one thing and scan as another.
 */

import type { CardProfile } from "@/lib/api";
import { cardFace } from "@/lib/card-face";
import { safeFilename } from "@/lib/downloads";
import { formatPhone } from "@/lib/phone";
import { encodeQr, type QrMatrix, qrToSvg } from "@/lib/qr";
import { zipBytes, type ZipEntry } from "@/lib/zip";

/** Norican Blue, brand guide 3.0 p.21: RGB #03193d, CMYK 100/70/0/60. */
const NORICAN_BLUE_RGB = "#03193d";
const NORICAN_BLUE_CMYK = "1 0.7 0 0.6";
const QUIET_ZONE = 4;
/** Side of every QR PDF, white margin included. */
export const QR_PRINT_MM = 20;
/** Smaller than this, the modules of a full vCard get too fine for a phone camera. */
const QR_MIN_MM = 18;

const COLUMNS = [
  "Name",
  "Title",
  "Email",
  "Tel",
  "Mobile",
  "Company",
  "Street",
  "City",
  "Country",
  "Website",
  "@QR",
] as const;

/** Most addresses per export; the backend's CARD_BATCH_MAX. */
export const PRINT_MAX = 100;

/**
 * The e-mail addresses in the paste box, each once, in order. The export
 * goes by address only, never by name: whatever else was pasted (an Excel
 * column, Outlook recipients with their display names) is ignored.
 */
export function printAddresses(text: string): string[] {
  const seen = new Set<string>();
  const addresses: string[] = [];
  for (const [address] of text.matchAll(/[^\s<>()[\],;:"']+@[^\s<>()[\],;:"']+\.[a-z]{2,}/gi)) {
    if (!seen.has(address.toLowerCase())) {
      seen.add(address.toLowerCase());
      addresses.push(address);
    }
  }
  return addresses;
}

/** The ZIP for `cards` (in that order), stamped with `created`. */
export function printPackage(cards: CardProfile[], created: Date): Blob {
  const encoder = new TextEncoder();
  const used = new Set<string>();
  const rows: string[][] = [];
  const files: ZipEntry[] = [];
  for (const card of cards) {
    const base = uniqueName(`${safeFilename(card.display_name)}-qr`, used);
    // The symbol "QR as SVG" exports: the full vCard at error correction M.
    const matrix = encodeQr(card.vcard, "M");
    files.push({ name: `${base}.pdf`, data: encoder.encode(qrPdf(matrix)) });
    files.push({
      name: `svg/${base}.svg`,
      data: encoder.encode(qrToSvg(matrix, { dark: NORICAN_BLUE_RGB, quietZone: QUIET_ZONE })),
    });
    rows.push([...printFields(card), `${base}.pdf`]);
  }
  const table = [[...COLUMNS], ...rows].map((row) => row.map(cell).join("\t")).join("\r\n");
  return new Blob(
    [
      zipBytes(
        [
          { name: "README.txt", data: encoder.encode(readme(cards.length, created)) },
          { name: "cards.txt", data: utf16le(`${table}\r\n`) },
          ...files,
        ],
        created,
      ),
    ],
    { type: "application/zip" },
  );
}

/** One `cards.txt` row without the QR file: the face's texts, both numbers on their own. */
export function printFields(card: CardProfile): string[] {
  const face = cardFace(card);
  const [local, domain] = face.email;
  return [
    face.name,
    face.jobTitle ?? "",
    domain ? `${local}@${domain}` : local,
    card.business_phone ? formatPhone(card.business_phone) : "",
    card.mobile_phone ? formatPhone(card.mobile_phone) : "",
    face.company,
    face.street ?? "",
    face.cityLine,
    face.country ?? "",
    face.website,
  ];
}

/**
 * The QR as a one-page vector PDF: `QR_PRINT_MM` square, dark modules filled
 * in Norican Blue CMYK, the quiet zone left as paper. Rows are drawn as runs
 * of adjacent modules, so the page has no hairline seams.
 */
export function qrPdf(matrix: QrMatrix): string {
  const page = num((QR_PRINT_MM / 25.4) * 72);
  const scale = num(((QR_PRINT_MM / 25.4) * 72) / (matrix.size + QUIET_ZONE * 2));
  // Flip y so module rows run top-down, as in `matrix.modules`.
  let content = `${NORICAN_BLUE_CMYK} k\n${scale} 0 0 -${scale} 0 ${page} cm\n`;
  matrix.modules.forEach((row, y) => {
    for (let x = 0; x < row.length; ) {
      if (!row[x]) {
        x++;
        continue;
      }
      const start = x;
      while (row[x]) {
        x++;
      }
      content += `${start + QUIET_ZONE} ${y + QUIET_ZONE} ${x - start} 1 re\n`;
    }
  });
  content += "f\n";
  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${page} ${page}] /Resources << >> /Contents 4 0 R >>`,
    `<< /Length ${content.length} >>\nstream\n${content}endstream`,
  ];
  let pdf = "%PDF-1.4\n";
  const offsets = objects.map((object, index) => {
    const offset = pdf.length;
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
    return offset;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  pdf += offsets.map((offset) => `${String(offset).padStart(10, "0")} 00000 n \n`).join("");
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return pdf;
}

function readme(count: number, created: Date): string {
  const day = created.toISOString().slice(0, 10);
  return [
    "Norican business cards: print package",
    "",
    `Exported on ${day} from the digital business card (Entra ID directory), ${count} ${count === 1 ? "colleague" : "colleagues"}.`,
    "",
    "cards.txt     Data source for InDesign Data Merge (tab separated, Unicode).",
    "              One row per colleague with the texts exactly as the digital card shows them.",
    "              The @QR column names the QR file of that row.",
    "*-qr.pdf      The QR code per colleague: vector, Norican Blue CMYK 100/70/0/60,",
    `              ${QR_PRINT_MM} x ${QR_PRINT_MM} mm including its white margin.`,
    "svg/*.svg     The same QR codes in RGB for screen use.",
    "",
    "How to set the cards",
    "1. Unzip into one folder. Keep cards.txt next to the PDF files.",
    "2. Open the Norican business card template in InDesign.",
    "3. Window > Utilities > Data Merge > Select Data Source: cards.txt.",
    "4. Place the text fields in the template and the @QR field in the QR frame.",
    "   Content Placement Options: fit images proportionally.",
    "5. Create Merged Document.",
    "",
    "Printing the QR",
    `- Print it ${QR_PRINT_MM} mm wide, at least ${QR_MIN_MM} mm, and keep the white margin around it.`,
    "- Tel or Mobile is empty for colleagues without that number; drop its label on those cards.",
    "- Do not retype or edit the texts. The QR carries the same data, and a change on one side",
    "  makes the card and its QR disagree. Wrong details are corrected in the Entra profile",
    "  (IT service desk); then export again.",
    "- The QR holds the details as of the export date. Export again for a later print run.",
    "",
  ].join("\r\n");
}

/** A Data Merge cell: no tabs or line breaks inside (a street can have two lines). */
function cell(value: string): string {
  return value
    .split(/\r?\n/)
    .map((part) => part.trim())
    .filter(Boolean)
    .join(", ")
    .replace(/\t/g, " ");
}

function uniqueName(base: string, used: Set<string>): string {
  let name = base;
  for (let n = 2; used.has(name.toLowerCase()); n++) {
    name = `${base}-${n}`;
  }
  used.add(name.toLowerCase());
  return name;
}

function utf16le(text: string): Uint8Array {
  const out = new Uint8Array(2 + text.length * 2);
  out[0] = 0xff;
  out[1] = 0xfe;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    out[2 + i * 2] = code & 0xff;
    out[3 + i * 2] = code >> 8;
  }
  return out;
}

function num(value: number): string {
  return String(Math.round(value * 10000) / 10000);
}
